import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle,
  HouseLine,
  IdentificationCard,
  PaperPlaneTilt,
  Phone,
  User,
} from '@phosphor-icons/react'

import KioskHeader from '../../components/KioskHeader/KioskHeader'
import useKioskAutoHome from '../../hooks/useKioskAutoHome'
import { useLang } from '../../context/LangContext'

import logo from '../../assets/images/logo.png'

const CONTENT = {
  en: {
    pageTitle: 'Join PBB Member',
    pageSubtitle: 'Berkhidmat Untuk Rakyat',

    title: 'Berkhidmat Untuk Rakyat',
    subtitle:
      'Register your interest to join PBB N22 Kedup. Our team will contact you for the next step.',

    name: 'Name',
    phone: 'Phone Number',
    email: 'Email',
    birthYear: 'Year of birth',
    address: 'Residence Address',

    namePlaceholder: 'eg. Martin',
    phonePlaceholder: '161234567',
    emailPlaceholder: 'eg. n22kedup@gmail.com',
    birthYearPlaceholder: 'eg. 1985',
    addressPlaceholder:
      'eg. Lot 358, Jalan Kuching, 94700 Serian.',

    backHome: 'Back to Home',
    submit: 'Join Us',

    successTitle: 'Thank you.',
    successDesc:
      'Your registration interest has been submitted. Our team will contact you shortly.',

    phoneRequired: 'Phone number is required',
    invalidPhone: 'Please enter a valid Malaysia mobile number',
  },

  bm: {
    pageTitle: 'Sertai Ahli PBB',
    pageSubtitle: 'Berkhidmat Untuk Rakyat',

    title: 'Berkhidmat Untuk Rakyat',
    subtitle:
      'Daftar minat anda untuk menyertai PBB N22 Kedup. Pasukan kami akan menghubungi anda untuk langkah seterusnya.',

    name: 'Nama',
    phone: 'Nombor Telefon',
    email: 'E-mel',
    birthYear: 'Tahun lahir',
    address: 'Alamat Rumah',

    namePlaceholder: 'contoh. Martin',
    phonePlaceholder: '161234567',
    emailPlaceholder: 'contoh. n22kedup@gmail.com',
    birthYearPlaceholder: 'contoh. 1985',
    addressPlaceholder:
      'contoh. Lot 358, Jalan Kuching, 94700 Serian.',

    backHome: 'Kembali ke Halaman Utama',
    submit: 'Sertai PBB',

    successTitle: 'Terima kasih.',
    successDesc:
      'Permohonan minat anda telah dihantar. Pasukan kami akan menghubungi anda sebentar lagi.',

    phoneRequired: 'Nombor telefon diperlukan',
    invalidPhone: 'Sila masukkan nombor telefon Malaysia yang sah',
  },
}

export default function JoinPbbPage() {
  useKioskAutoHome()

  const navigate = useNavigate()
  const { lang } = useLang()

  const t = CONTENT[lang] || CONTENT.en

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [birthYear, setBirthYear] = useState('')
  const [address, setAddress] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const [phoneError, setPhoneError] = useState('')

  const isPhoneValid = /^1\d{8,9}$/.test(phone)

  const canSubmit =
    name.trim() &&
    isPhoneValid &&
    birthYear.trim() &&
    address.trim()

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '')

    if (value.startsWith('60')) value = value.slice(2)
    if (value.startsWith('0')) value = value.slice(1)
    if (value.length > 10) value = value.slice(0, 10)

    setPhone(value)

    if (!value) {
      setPhoneError(t.phoneRequired)
    } else if (!/^1\d{8,9}$/.test(value)) {
      setPhoneError(t.invalidPhone)
    } else {
      setPhoneError('')
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault()

    if (!canSubmit) return

    const fullPhoneNumber = `+60${phone}`

    // TODO: connect to backend API
    // Example payload:
    // {
    //   name,
    //   phone: fullPhoneNumber,
    //   email,
    //   birthYear,
    //   address,
    //   source: 'kiosk'
    // }

    console.log({
      name,
      phone: fullPhoneNumber,
      email,
      birthYear,
      address,
      source: 'kiosk',
    })

    setSubmitted(true)

    window.setTimeout(() => {
      navigate('/', { replace: true })
    }, 3500)
  }

  return (
    <main className="kiosk-page join-pbb-page">
      <div className="app-container kiosk-container">
        <KioskHeader
          title={t.pageTitle}
          subtitle={t.pageSubtitle}
        />

        <section className="join-pbb-card">
          {!submitted ? (
            <form onSubmit={handleSubmit}>
              <div className="join-pbb-card__brand">
                <img
                  src={logo}
                  alt="PBB N22 Kedup"
                />

                <span>N22 Kedup</span>
              </div>

              <div className="join-pbb-card__header">
                <span className="join-pbb-card__icon">
                  <IdentificationCard size={30} />
                </span>

                <div>
                  <h1>{t.title}</h1>
                  <p>{t.subtitle}</p>
                </div>
              </div>

              <div className="join-pbb-form">
                <div className="form-group">
                  <label className="form-label">
                    <User size={15} /> {t.name}{' '}
                    <strong>*</strong>
                  </label>

                  <input
                    className="form-input"
                    value={name}
                    onChange={(event) =>
                      setName(event.target.value)
                    }
                    placeholder={t.namePlaceholder}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <Phone size={15} /> {t.phone}{' '}
                    <strong>*</strong>
                  </label>

                  <div className="phone-input phone-input--malaysia">
                    <div className="phone-input__prefix">
                      🇲🇾 +60
                    </div>

                    <input
                      type="tel"
                      inputMode="numeric"
                      className={`phone-input__number ${
                        phoneError ? 'is-error' : ''
                      }`}
                      placeholder={t.phonePlaceholder}
                      value={phone}
                      onChange={handlePhoneChange}
                      onBlur={handlePhoneChange}
                    />
                  </div>

                  {phoneError && (
                    <div className="form-error">
                      {phoneError}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {t.email}
                  </label>

                  <input
                    className="form-input"
                    type="email"
                    inputMode="email"
                    value={email}
                    onChange={(event) =>
                      setEmail(event.target.value)
                    }
                    placeholder={t.emailPlaceholder}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {t.birthYear}{' '}
                    <strong>*</strong>
                  </label>

                  <input
                    className="form-input"
                    type="number"
                    inputMode="numeric"
                    value={birthYear}
                    onChange={(event) =>
                      setBirthYear(event.target.value)
                    }
                    placeholder={t.birthYearPlaceholder}
                  />
                </div>

                <div className="form-group form-group--full">
                  <label className="form-label">
                    <HouseLine size={15} /> {t.address}{' '}
                    <strong>*</strong>
                  </label>

                  <textarea
                    className="form-textarea"
                    rows={5}
                    value={address}
                    onChange={(event) =>
                      setAddress(event.target.value)
                    }
                    placeholder={t.addressPlaceholder}
                  />
                </div>
              </div>

              <div className="join-pbb-actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => navigate('/')}
                >
                  {t.backHome}
                </button>

                <button
                  type="submit"
                  className="btn btn--primary join-pbb-submit"
                  disabled={!canSubmit}
                >
                  <PaperPlaneTilt size={18} /> {t.submit}
                </button>
              </div>
            </form>
          ) : (
            <div className="kiosk-success-state join-pbb-success">
              <CheckCircle size={58} />

              <h1>{t.successTitle}</h1>

              <p>{t.successDesc}</p>
            </div>
          )}
        </section>
      </div>
    </main>
  )
}